import { Client, StompSubscription } from "@stomp/stompjs";
import { Reservation, RunningJob, Weighing } from "@q-food/q-food";

export interface Channel<T> {
	readonly destination: string;
	readonly decode: (body: string) => T;
}

/** A channel whose body is JSON of type [T]. */
function jsonChannel<T>(destination: string): Channel<T> {
	return { destination, decode: (body) => JSON.parse(body) as T };
}

/** A channel whose body is a plain (non-JSON) string. */
function textChannel(destination: string): Channel<string> {
	return { destination, decode: (body) => body };
}

/** A channel that only signals "something happened" — the (often empty) body carries nothing. */
function signalChannel(destination: string): Channel<void> {
	return { destination, decode: () => undefined };
}

/**
 * Subscribe a STOMP [client] to a typed [channel]; the handler receives the decoded payload and the
 * [StompSubscription] is returned so callers that track their own subscriptions can push it. Used by
 * views that subscribe directly on the shared client (rather than via useSocketSubscriber).
 */
export function subscribeClientTo<T>(
	client: Client,
	channel: Channel<T>,
	handler: (payload: T) => void,
): StompSubscription {
	return client.subscribe(channel.destination, (message) => handler(channel.decode(message.body)));
}

// ---------------------------------------------------------------------------------------------------
// Websocket-only payload types (not in the generated api client — mirrored from their Kotlin sources)
// ---------------------------------------------------------------------------------------------------

export interface OpenGridSignal {
	resource: string;
	criteria: string;
}

export interface ReservationsChangedSignal {
	type: string;
	triggeredByUserId: string | null;
}

export interface NotificationSignal {
	type: string;
	title: string;
	message?: string;
	details?: string[];
	priority?: boolean;
}

export interface ProductionViewMessage {
	type: "CONTEXT";
	activeResultLotId: string | null;
	activeReservationId: string | null;
}

/** Which grid resource changed and which of its rows, so grids can patch just those rows in place. */
export interface GridRowsChangedSignal {
	resource: string;
	rowIds: string[];
}

// ---------------------------------------------------------------------------------------------------
// Per-user channels (subscribed as `/user/channel/...` on the shared client)
// ---------------------------------------------------------------------------------------------------

export const userNotifications = jsonChannel<NotificationSignal>("/user/channel/notifications");
export const userRefreshGrid = jsonChannel<string[]>("/user/channel/refresh-grid");
export const userOpenGrid = jsonChannel<OpenGridSignal>("/user/channel/open-grid");
export const userRefreshApp = signalChannel("/user/channel/refresh-app");
export const userRefreshUserActiveLanguage = textChannel("/user/channel/refresh-user-active-language");
export const userRefreshCompanyActiveLanguage = textChannel("/user/channel/refresh-company-active-language");

// ---------------------------------------------------------------------------------------------------
// Production-step channels
// ---------------------------------------------------------------------------------------------------

export const productionStepView = (stepId: string) =>
	jsonChannel<ProductionViewMessage>(`/channel/production-steps/${stepId}`);
export const productionStepResources = (stepId: string) =>
	jsonChannel<Reservation>(`/channel/production-steps/${stepId}/resources`);
export const productionStepReservationsChanged = (stepId: string) =>
	jsonChannel<ReservationsChangedSignal>(`/channel/production-steps/${stepId}/sales-order-row-reservations`);
export const productionStepViewContext = (stepId: string) =>
	signalChannel(`/channel/production-steps/${stepId}/view-context`);
export const productionTotals = (companyId: string) => signalChannel(`/channel/production-totals/${companyId}`);

// ---------------------------------------------------------------------------------------------------
// Print-fleet channel
// ---------------------------------------------------------------------------------------------------

/**
 * Signalled when a print queue's worst severity changes for this company — a queue stopping, a job
 * getting stuck, or either clearing. Carries nothing: the board refetches, and that fetch is
 * company-scoped server-side.
 */
export const printFleet = (companyId: string) => signalChannel(`/channel/print-fleet/${companyId}`);

// ---------------------------------------------------------------------------------------------------
// Grid row-update channel
// ---------------------------------------------------------------------------------------------------

/**
 * Broadcast (to every connected client) that specific rows of a grid resource changed and should be
 * patched in place — an AG Grid server-side transaction update rather than a full grid refetch. Carries
 * only ids: a client ignores any row it doesn't have loaded and refetches the rest through its own
 * ACL-scoped endpoint, so a row or field a user may not see is never returned.
 */
export const gridRowUpdates = jsonChannel<GridRowsChangedSignal>("/channel/grid-row-updates");

// ---------------------------------------------------------------------------------------------------
// Application-update channel
// ---------------------------------------------------------------------------------------------------

/**
 * Broadcast (to every connected client) that the backend is shutting down for an update. The client
 * disables the whole screen with a blocking, centered "the application is being updated" modal and closes it automatically once the backend is reachable again (the
 * socket reconnect, or a health-check ping as a fallback). Carries nothing — it is purely a signal.
 */
export const appUpdate = signalChannel("/channel/app-update");

// ---------------------------------------------------------------------------------------------------
// Scale channels
// ---------------------------------------------------------------------------------------------------

export const scaleRegistrations = (scaleId: string) => jsonChannel<Weighing>(`/channel/scale/${scaleId}/registrations`);
export const scaleRealtime = (scaleId: string) => jsonChannel<Weighing>(`/channel/scale/${scaleId}/realtime`);

// ---------------------------------------------------------------------------------------------------
// Running-jobs channels
// ---------------------------------------------------------------------------------------------------

/**
 * The full set of long-running, user-triggered jobs currently in progress — one topic per scope, so a
 * client filters by subscribing rather than by inspecting the payload. Each message carries the whole
 * set for its scope, so a handler replaces that scope's state outright. Used to disable a job's trigger
 * button for everyone while it runs; the refresh-safe catch-up read is `getRunningJobs()`.
 */
export const runningJobs = (companyId: string) => jsonChannel<RunningJob[]>(`/channel/running-jobs/${companyId}`);
export const globalRunningJobs = jsonChannel<RunningJob[]>("/channel/running-jobs/global");
